import React, { MouseEvent, ReactNode, useMemo, useState } from 'react';
import { Button, SxProps, Theme, Typography, styled } from '@mui/material';
import { FocusTheme } from '@/components/Focus/configs/theme';
import ScheduleAvailChipMenu, { ChipMenuItem } from './ScheduleAvailChipMenu';

type ReviewScheduleAvailChipButtonProps = {
  caption: ReactNode;
  icon: ReactNode;
  items: ChipMenuItem[];
  label?: string;
  onClick?: () => void;
  sx?: SxProps<Theme>;
};

const ScheduleAvailChipButton = ({ caption, icon, items, label, onClick, sx }: ReviewScheduleAvailChipButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const open = Boolean(anchorEl);

  const hasMenu = useMemo(() => !!items?.length, [items]);
  const hasLabel = Boolean(label);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (onClick) {
      onClick();
      return;
    }
  };

  const openMenu: React.PointerEventHandler = (event) => {
    if (!hasMenu) return;

    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => setAnchorEl(null);

  const menu = hasMenu ? <ScheduleAvailChipMenu items={items} anchorEl={anchorEl} open={open} /> : null;

  return (
    <div onPointerOver={openMenu} onPointerOut={closeMenu}>
      <StyledButton startIcon={<IconWrapper>{icon}</IconWrapper>} variant="text" onClick={handleClick} hasLabel={hasLabel} sx={sx}>
        {label ? <StyledLabel>{label}</StyledLabel> : null}
        <StyledCount hasLabel={hasLabel}>{caption}</StyledCount>
      </StyledButton>
      {menu}
    </div>
  );
};

export default ScheduleAvailChipButton;

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'hasLabel'
})<{ hasLabel: boolean }>`
  min-width: 55px;
  padding: ${({ theme, hasLabel }) => (hasLabel ? `${theme.spacing(0.25)} ${theme.spacing(1.5)}` : `${theme.spacing(0.5)} ${theme.spacing(0.75)}`)};
  border: 2px solid ${({ theme }) => theme.palette.background.default};

  .MuiButton-startIcon {
    margin-right: ${({ theme, hasLabel }) => (hasLabel ? theme.spacing(1) : theme.spacing(0.75))};
    margin-left: 0;
  }
`;

const IconWrapper = styled('span')`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const StyledLabel = styled(Typography)`
  font-size: 16px;
  margin-right: 12px;
  color: ${({ theme }) => theme.palette.text.primary};
  white-space: nowrap;
`;

const StyledCount = styled('span', {
  shouldForwardProp: (prop) => prop !== 'hasLabel'
})<{ hasLabel: boolean }>`
  font-size: 16px;
  color: ${({ theme, hasLabel }) => (hasLabel ? (theme as FocusTheme).palette.neutral[20] : theme.palette.text.primary)};
`;
